import { useEffect, useRef } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigation } from 'react-router';
import { useWorkspace } from '../../hooks/useWorkspace';
import { ReadOnlyBanner } from '../../components/shared';
import { LogoMark } from './Logo';
import { SkipLink } from './SkipLink';
import { BottomNav, Sidebar } from './Navigation';
import { UserMenu } from './UserMenu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
// Platform module mount points (bell, offline banner, screen-view analytics).
import { NotificationBell } from '../../features/platform/notifications/NotificationBell';
import { OfflineBanner } from '../../features/platform/offline/OfflineBanner';
import { PageViewTracker } from '../../features/platform/analytics/PageViewTracker';

/**
 * Workspace layout: header (switcher, notifications, account), sidebar on desktop (>= 1024px),
 * bottom tab bar on phones, read-only banner, and focus management on navigation.
 */
export function AppShell() {
  const { wsId } = useWorkspace();
  const location = useLocation();
  const navigation = useNavigation();
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  // Move focus to the new page's heading after client-side navigation so screen readers announce it.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const main = mainRef.current;
    if (!main) return;
    const heading = main.querySelector<HTMLElement>('[data-page-heading], h1');
    (heading ?? main).focus({ preventScroll: false });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);


  return (
    <div className="flex min-h-dvh flex-col">
      <SkipLink />
      {navigation.state === 'loading' && (
        <div className="st-animate-pulse fixed inset-x-0 top-0 z-[70] h-1 bg-primary" role="progressbar" aria-label="Loading page" />
      )}
      <header className="sticky top-0 z-30 border-b border-border bg-surface">
        <div className="flex min-h-16 items-center gap-2 px-3 lg:px-4">
          <RouterLink to={`/w/${wsId}`} className="shrink-0 no-underline" aria-label="Seventide home">
            <LogoMark />
          </RouterLink>
          <span className="hidden text-xl font-bold lg:inline">Seventide</span>
          <div className="mx-1 hidden h-8 w-px bg-border lg:block" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <WorkspaceSwitcher />
          </div>
          <NotificationBell wsId={wsId} />
          <UserMenu wsId={wsId} />
        </div>
      </header>
      <ReadOnlyBanner />
      <OfflineBanner />
      <PageViewTracker />
      <div className="flex flex-1">
        <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-72 shrink-0 border-r border-border bg-surface lg:block">
          <Sidebar className="h-full" />
        </aside>
        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="mx-auto w-full max-w-5xl min-w-0 flex-1 px-4 pt-6 pb-28 outline-none lg:px-8 lg:pb-10"
        >
          <Outlet />
        </main>
      </div>
      <BottomNav className="fixed inset-x-0 bottom-0 z-30 lg:hidden" />
    </div>
  );
}
